import React, { useMemo, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useIntl } from '../../../util/reactIntl';
import { updateSearchPageWindowFlag } from '../helper';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox, FieldTextInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';
import { Form } from 'react-final-form';
import _ from 'lodash';
import { unstable_batchedUpdates } from 'react-dom';

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const reorderOptions = (optionsList, selected) => {
  const selectedSet = new Set(selected);
  const selectedItems = optionsList.filter(opt => selectedSet.has(opt.option));
  const unselectedItems = optionsList.filter(opt => !selectedSet.has(opt.option));
  return [...selectedItems, ...unselectedItems];
};
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, onChange, selectedOptions } = props;
  const handleChange = option => e => {
    if (typeof onChange === 'function') {
      onChange(e, option);
    }
  };

  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {options.map(optionConfig => {
          const { option, label } = optionConfig;
          const fieldId = `${id}.${option}`;
          const checked =
            selectedOptions && selectedOptions instanceof Set
              ? selectedOptions.has(option)
              : undefined;
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={name}
                label={label}
                value={option}
                onChange={handleChange(option)}
                checked={checked}
              />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const history = useHistory();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    param,
    urlQueryParams, // NEW: All URL query params
    ...rest
  } = props;
  const queryParamName = getQueryParamName(queryParamNames);
  const classes = classNames(rootClassName || css.root, className);

  const { location } = rest;

  // Extract brand slug(s) from URL - check for both /brands/ and /brand/ (in category pages)
  const isBrandPage =
    location?.pathname?.includes('/brands/') || location?.pathname?.includes('/brand/');
  const brandSlugFromUrl = isBrandPage
    ? location.pathname.includes('/brands/')
      ? location.pathname
          .split('/brands/')[1]
          ?.split('/')[0]
          ?.split('?')[0]
      : location.pathname
          .split('/brand/')[1]
          ?.split('/')[0]
          ?.split('?')[0]
    : null;

  // Support multiple brands in URL: /brands/adidas,puma or /brand/adidas,puma
  const brandSlugsArray = brandSlugFromUrl ? brandSlugFromUrl.split(',') : [];

  // Extract category from URL - handle multi-level categories
  const isCategoryPage = location?.pathname?.includes('/category/');
  const categoryPathSegments = isCategoryPage
    ? location.pathname
        .split('/category/')[1]
        ?.split('/brand/')[0] // Remove brand part if exists
        ?.split('?')[0] // Remove query params
        ?.split('/')
        .filter(Boolean)
    : [];

  // Helper to check if this is a category filter at a specific level
  const getCategoryLevel = () => {
    if (!name.startsWith('pub_categoryLevel')) return null;
    const match = name.match(/pub_categoryLevel(\d)/);
    return match ? parseInt(match[1]) : null;
  };
  const categoryLevel = getCategoryLevel();

  const [showAllColors, setShowAllColors] = useState(false);
  const [showAllbrands, setShowAllbrands] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [localSelectedOptions, setLocalSelectedOptions] = useState(() => {
    const value = initialValues[queryParamName] || '';
    const initialSet = new Set(value.split(',').filter(Boolean));

    // If on brand page and it's the brand filter, add brands from URL
    if (isBrandPage && name.toLowerCase() === 'brand' && brandSlugsArray.length > 0) {
      brandSlugsArray.forEach(slug => {
        const matchingOption = options.find(opt => opt.option === slug);
        if (matchingOption) {
          initialSet.add(matchingOption.option);
        }
      });
    }

    // If on category page and it's a category filter, add category from URL
    if (isCategoryPage && categoryLevel && categoryPathSegments.length >= categoryLevel) {
      const categorySlug = categoryPathSegments[categoryLevel - 1];
      if (categorySlug) {
        const matchingOption = options.find(opt => {
          const optSlug = opt.option
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/_/g, '-');
          return optSlug === categorySlug.toLowerCase();
        });
        if (matchingOption) {
          initialSet.clear(); // Categories are exclusive - only one per level
          initialSet.add(matchingOption.option);
        }
      }
    }

    return { [queryParamName]: initialSet };
  });

  useEffect(() => {
    const value = initialValues[queryParamName] || '';
    const newSet = new Set(value.split(',').filter(Boolean));

    // If on brand page and it's the brand filter, add brands from URL
    if (isBrandPage && name.toLowerCase() === 'brand' && brandSlugsArray.length > 0) {
      brandSlugsArray.forEach(slug => {
        const matchingOption = options.find(opt => opt.option === slug);
        if (matchingOption) {
          newSet.add(matchingOption.option);
        }
      });
    }

    // If on category page and it's a category filter, add category from URL
    if (isCategoryPage && categoryLevel && categoryPathSegments.length >= categoryLevel) {
      const categorySlug = categoryPathSegments[categoryLevel - 1];
      if (categorySlug) {
        const matchingOption = options.find(opt => {
          const optSlug = opt.option
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/_/g, '-');
          return optSlug === categorySlug.toLowerCase();
        });
        if (matchingOption) {
          newSet.clear(); // Categories are exclusive - only one per level
          newSet.add(matchingOption.option);
        }
      }
    }

    setLocalSelectedOptions({ [queryParamName]: newSet });
  }, [initialValues[queryParamName], brandSlugFromUrl, categoryPathSegments.join('/')]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchPopupQuery, setSearchPopupQuery] = useState('');

  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  let selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  // If on brand page and it's the brand filter, ensure the brands from URL are selected
  if (isBrandPage && name.toLowerCase() === 'brand' && brandSlugsArray.length > 0) {
    brandSlugsArray.forEach(slug => {
      const matchingOption = options.find(opt => opt.option === slug);

      if (matchingOption && !selectedOptions.includes(matchingOption.option)) {
        selectedOptions = [...selectedOptions, matchingOption.option];
      }
    });
  }

  // If on category page and it's a category filter, ensure the category from URL is selected
  if (isCategoryPage && categoryLevel && categoryPathSegments.length >= categoryLevel) {
    const categorySlug = categoryPathSegments[categoryLevel - 1];
    if (categorySlug) {
      const matchingOption = options.find(opt => {
        const optSlug = opt.option
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/_/g, '-');
        return optSlug === categorySlug.toLowerCase();
      });

      if (matchingOption) {
        // Categories are exclusive - replace existing selection
        selectedOptions = [matchingOption.option];
      }
    }
  }

  const reorderedOptions = reorderOptions(options, selectedOptions);

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
    : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const saveMobileScrollPos = () => {
    if (typeof window !== 'undefined') {
      const el =
        document.querySelector('[class*="scrollLayer"]') ||
        document.querySelector('[class*="modalContainer"]');
      const containerScroll = el ? el.scrollTop : 0;
      const winScroll = window.scrollY || window.pageYOffset || 0;

      if (containerScroll > 0 || winScroll > 0) {
        updateSearchPageWindowFlag({
          mobileFilterScrollPos: containerScroll,
          mobileWindowScrollPos: winScroll,
        });
      }
    }
  };

  const handleSubmit = values => {
    saveMobileScrollPos();
    const usedValue = values ? values[name] : values;

    // Special handling for category filters (pub_categoryLevel1, pub_categoryLevel2, pub_categoryLevel3)
    if (categoryLevel) {
      // Get all current category values from the URL or initialValues
      const currentCategories = {
        level1: null,
        level2: null,
        level3: null,
      };

      // Extract from URL if on category page
      if (isCategoryPage && categoryPathSegments.length > 0) {
        currentCategories.level1 = categoryPathSegments[0] || null;
        currentCategories.level2 = categoryPathSegments[1] || null;
        currentCategories.level3 = categoryPathSegments[2] || null;
      } else if (urlQueryParams) {
        // Get existing category values from urlQueryParams
        const level1Value = urlQueryParams.pub_categoryLevel1;
        const level2Value = urlQueryParams.pub_categoryLevel2;
        const level3Value = urlQueryParams.pub_categoryLevel3;

        if (level1Value) {
          currentCategories.level1 = level1Value;
        }
        if (level2Value) {
          currentCategories.level2 = level2Value;
        }
        if (level3Value) {
          currentCategories.level3 = level3Value;
        }
      }

      // Update the current level with the selected value
      if (!usedValue || usedValue.length === 0) {
        // Deselecting this level - clear this level and all levels below
        for (let i = categoryLevel; i <= 3; i++) {
          currentCategories[`level${i}`] = null;
        }
      } else {
        // Only one category per level - use the first selected option
        const selectedCategory = usedValue[0];
        const categoryOption = options.find(opt => opt.option === selectedCategory);
        const categoryValue = categoryOption ? categoryOption.option : null;

        currentCategories[`level${categoryLevel}`] = categoryValue;

        // Clear all levels below the current one when changing category
        for (let i = categoryLevel + 1; i <= 3; i++) {
          currentCategories[`level${i}`] = null;
        }
      }

      // Build the category path
      const categoryPath = [
        currentCategories.level1,
        currentCategories.level2,
        currentCategories.level3,
      ]
        .filter(Boolean)
        .join('/');

      if (!categoryPath) {
        // No category selected - go back to search
        updateSearchPageWindowFlag({ preventLoading: false });
        history.push(`/s${location.search}`);
        return;
      }

      // Check if we also have a brand in the URL
      const brandMatch = location.pathname.match(/\/brand\/([^/?]+)/);
      if (brandMatch) {
        // Preserve brand in the URL
        updateSearchPageWindowFlag({ preventLoading: false });
        history.push(`/category/${categoryPath}/brand/${brandMatch[1]}${location.search}`);
      } else {
        updateSearchPageWindowFlag({ preventLoading: false });
        history.push(`/category/${categoryPath}${location.search}`);
      }
      return;
    }

    // Special handling for brand filter
    if (name.toLowerCase() === 'brand') {
      if (!usedValue || usedValue.length === 0) {
        // No brand selected - go back to general search or category
        const categoryMatch = location.pathname.match(
          /\/category\/([^/]+(?:\/[^/]+)*?)(?:\/brand\/|$)/
        );
        if (categoryMatch) {
          updateSearchPageWindowFlag({ preventLoading: false });

          history.push(`/category/${categoryMatch[1]}${location.search}`);
        } else if (isBrandPage) {
          updateSearchPageWindowFlag({ preventLoading: false });

          history.push(`/s${location.search}`);
        } else {
          // Use default behavior for search page
          onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
        }
        return;
      } else if (usedValue.length >= 1) {
        // Single or multiple brands - create comma-separated slug list using the option value (not label)
        const brandSlugs = usedValue
          .map(brandValue => {
            const brandOption = options.find(opt => opt.option === brandValue);
            // Use the option value directly (which is already the correct value like "adidas", "puma")
            return brandOption ? brandOption.option : null;
          })
          .filter(Boolean)
          .join(',');

        if (brandSlugs) {
          // Check if we're on a category page - match multi-level categories
          const categoryMatch = location.pathname.match(
            /\/category\/([^/]+(?:\/[^/]+)*?)(?:\/brand\/|$)/
          );
          if (categoryMatch) {
            updateSearchPageWindowFlag({ preventLoading: false });

            // Navigate to /category/{category-path}/brand/{brand1,brand2,...}
            const categoryPath = categoryMatch[1];
            history.push(`/category/${categoryPath}/brand/${brandSlugs}${location.search}`);
          } else {
            updateSearchPageWindowFlag({ preventLoading: false });
            // Navigate to /brands/{brand1,brand2,...}
            history.push(`/brands/${brandSlugs}${location.search}`);
          }
          return;
        }
      }
    }

    // For non-brand filters on brand/category pages, preserve the current path
    if (
      (isBrandPage || location?.pathname?.includes('/category/')) &&
      name.toLowerCase() !== 'brand'
    ) {
      const formattedParams = format(usedValue, queryParamName, schemaType, searchMode);
      const searchParams = new URLSearchParams(location.search);

      // Update the specific filter parameter
      if (formattedParams[queryParamName]) {
        searchParams.set(queryParamName, formattedParams[queryParamName]);
      } else {
        searchParams.delete(queryParamName);
      }

      // Preserve current path and update query params
      const newSearch = searchParams.toString();
      history.push(`${location.pathname}${newSearch ? '?' + newSearch : ''}`);
      return;
    }

    // Default behavior - use query params
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };
  const isLabelForBrandInSearch =
    name.toLowerCase() === 'brand' &&
    (location?.pathname?.includes('/s') ||
      location?.pathname?.includes('/brands/') ||
      location?.pathname?.includes('/category/'));
  const isLabelForColorInSearch =
    name.toLowerCase() === 'color' &&
    (location?.pathname?.includes('/s') ||
      location?.pathname?.includes('/brands/') ||
      location?.pathname?.includes('/category/'));

  //const brandLabelOptions = options.slice(0, 8);
  const brandDisplayLimit = 8;
  const totalbrandOptions = options.length;
  const hiddenbrandCount = totalbrandOptions - brandDisplayLimit;

  const visiblebrandOptions = showAllbrands
    ? reorderOptions(options, selectedOptions)
    : reorderOptions(options, selectedOptions).slice(0, brandDisplayLimit);

  //const colorLabelOptions = options.slice(0, 4);
  const colorDisplayLimit = 4;
  const totalColorOptions = options.length;
  const hiddenColorCount = totalColorOptions - colorDisplayLimit;

  const visibleColorOptions = showAllColors
    ? reorderOptions(options, selectedOptions)
    : reorderOptions(options, selectedOptions).slice(0, colorDisplayLimit);

  const brandLabelFilterOptions = _.cloneDeep(options);
  const colorLabelFilterOptions = _.cloneDeep(options);

  const filteredOptions = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    const filtered = brandLabelFilterOptions.filter(opt =>
      opt.label.toLowerCase().startsWith(lower)
    );
    return reorderOptions(filtered, selectedOptions);
  }, [searchQuery, brandLabelFilterOptions, selectedOptions]);

  const filteredPopupOptions = useMemo(() => {
    const lower = searchPopupQuery.toLowerCase();
    const filtered = brandLabelFilterOptions.filter(opt =>
      opt.label.toLowerCase().startsWith(lower)
    );
    return reorderOptions(filtered, selectedOptions);
  }, [searchPopupQuery, brandLabelFilterOptions, selectedOptions]);

  const hasNoSearchPopupFilters = searchPopupQuery && filteredPopupOptions.length === 0;
  const hasNoSearchFilters = searchQuery && filteredOptions.length === 0;

  const handlePopupClose = () => {
    const value = initialValues[queryParamName] || '';
    setLocalSelectedOptions({ [queryParamName]: new Set(value.split(',')) });
  };

  const handleCheckOption = (e, opt) => {
    const isChecked = e.target.checked;
    setLocalSelectedOptions(localOptions => {
      const options = localOptions[queryParamName];
      if (isChecked) {
        options.add(opt);
      } else {
        options.delete(opt);
      }
      return { [queryParamName]: options };
    });
  };

  const handlePopupClear = () => {
    // const { pub_brand = '' } = initialValues;
    // const newValues = new Set(pub_brand.split(','));
    unstable_batchedUpdates(() => {
      setSearchPopupQuery('');
      setLocalSelectedOptions(localOptions => {
        const options = localOptions[queryParamName];
        options.clear();
        return { [queryParamName]: options };
      });
    });
  };

  const handlePopupCancel = () => {
    setSearchPopupQuery('');
    setLocalSelectedOptions(_ => {
      const value = initialValues[queryParamName] || '';
      return { [queryParamName]: new Set(value.split(',')) };
    });
  };
  const handlePopupSubmit = () => {
    const values = localSelectedOptions[queryParamName];
    handleSubmit({ [name]: Array.from(values).filter(Boolean) });
  };

  return (
    <>
      {isLabelForBrandInSearch && (
        <FilterPlain
          className={className}
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(prev => !prev)}
          rootClassName={rootClassName}
          label={label}
          labelSelection={labelSelectionForPlain}
          isSelected={hasInitialValues}
          id={`${id}.plain`}
          liveEdit
          onSubmit={handleSubmit}
          initialValues={namedInitialValues}
          {...rest}
        >
          <>
            <div className={css.searchWrapperOutsidePopup}>
              <FieldTextInput
                input={searchQuery}
                name="brand-search-bar-outside-popup"
                placeholder="Search Brands"
                className={css.searchInputOutsidePopup}
                onChange={e => {
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
            {hasNoSearchFilters ? (
              <>Not Found</>
            ) : (
              <GroupOfFieldCheckboxes
                className={css.fieldGroupPlain}
                name={name}
                id={`${id}-checkbox-group`}
                options={
                  filteredOptions.length
                    ? filteredOptions.slice(0, brandDisplayLimit)
                    : visiblebrandOptions
                }
              />
            )}
          </>
        </FilterPlain>
      )}
      {showAsPopup ||
        (isLabelForBrandInSearch && (
          <FilterPopup
            // className={css.showmore}
            outsideClassName={css.popUpForBrand}
            rootClassName={rootClassName}
            popupClassName={css.popupSize}
            label={labelForPopup}
            customTriggerLabel={
              hasNoSearchFilters
                ? '  '
                : showAllbrands
                ? ' '
                : isFilterOpen
                ? `+${hiddenbrandCount} more`
                : ' '
            }
            isSelected={hasInitialValues}
            id={`${id}.popup`}
            showAsPopup
            contentPlacementOffset={contentPlacementOffset}
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            onClear={handlePopupClear}
            onClose={handlePopupClose}
            disableapply={hasNoSearchPopupFilters}
            initialValues={namedInitialValues}
            name={name}
            keepDirtyOnReinitialize
            {...rest}
          >
            <Form
              onSubmit={e => e.preventDefault()}
              render={() => (
                // <form>
                <div className={css.popupHeader}>
                  <input
                    value={searchPopupQuery}
                    name="filter-search-for-search-page"
                    placeholder="Search Brands"
                    className={css.searchInput}
                    // inputRef={searchRef}
                    onChange={e => {
                      setSearchPopupQuery(e.target.value);
                    }}
                  />
                </div>
                // </form>
              )}
            />
            <GroupOfFieldCheckboxes
              className={css.fieldGroup}
              name={name}
              id={`${id}-checkbox-group-popup`}
              options={filteredPopupOptions}
              onChange={handleCheckOption}
              selectedOptions={localSelectedOptions[queryParamName]}
            />
          </FilterPopup>
        ))}
      {!showAsPopup && !isLabelForBrandInSearch && !isLabelForColorInSearch && (
        <FilterPlain
          className={className}
          rootClassName={rootClassName}
          label={label}
          labelSelection={labelSelectionForPlain}
          isSelected={hasInitialValues}
          id={`${id}.plain`}
          liveEdit
          onSubmit={handleSubmit}
          initialValues={namedInitialValues}
          {...rest}
        >
          <GroupOfFieldCheckboxes
            className={css.fieldGroupPlain}
            name={name}
            id={`${id}-checkbox-group`}
            options={options}
          />
        </FilterPlain>
      )}
      {isLabelForColorInSearch && (
        <FilterPlain
          className={className}
          rootClassName={rootClassName}
          label={label}
          labelSelection={labelSelectionForPlain}
          isSelected={hasInitialValues}
          id={`${id}.plain`}
          liveEdit
          onSubmit={handleSubmit}
          initialValues={namedInitialValues}
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(prev => !prev)}
        >
          <GroupOfFieldCheckboxes
            className={css.fieldGroupPlain}
            name={name}
            id={`${id}-checkbox-group`}
            options={visibleColorOptions}
          />
        </FilterPlain>
      )}
      {showAsPopup ||
        (isLabelForColorInSearch && (
          <FilterPopup
            // className={css.showmore}
            outsideClassName={css.popUpForBrand}
            rootClassName={rootClassName}
            popupClassName={css.popupSize}
            label={labelForPopup}
            customTriggerLabel={
              showAllColors ? null : isFilterOpen ? `+${hiddenColorCount} more` : ' '
            }
            isSelected={hasInitialValues}
            id={`${id}.popup`}
            showAsPopup
            contentPlacementOffset={contentPlacementOffset}
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            onClose={handlePopupClose}
            // onClear={() => setSearchQuery('')}
            onClear={handlePopupClear}
            disableapply={hasNoSearchFilters}
            initialValues={namedInitialValues}
            name={name}
            keepDirtyOnReinitialize
            {...rest}
          >
            <Form
              onSubmit={e => e.preventDefault()}
              render={() => (
                // <form>
                <div className={css.popupHeader}>
                  <input
                    value={searchPopupQuery}
                    name="filter-search-for-search-page"
                    placeholder="Search Colors"
                    className={css.searchInput}
                    // inputRef={searchRef}
                    onChange={e => {
                      setSearchPopupQuery(e.target.value);
                    }}
                  />
                </div>
                // </form>
              )}
            />
            <GroupOfFieldCheckboxes
              className={css.fieldGroup}
              name={name}
              id={`${id}-checkbox-group-popup`}
              options={filteredPopupOptions}
              onChange={handleCheckOption}
              selectedOptions={localSelectedOptions[queryParamName]}
            />
          </FilterPopup>
        ))}
    </>
  );
};

export default SelectMultipleFilter;
